/**
 * The rule sheet's own copy of an exercise, and every transition over it.
 *
 * ── Draft-local until „Zastosuj do draftu", and that is the whole reason this exists ──────────
 * Picking a family, naming the column the client climbs and typing a step is three clicks and ONE
 * prescription. Writing each click through to the plan would put three entries on the undo stack,
 * recompute the matrix twice on the way to a state nobody asked to see, and let the shapes the
 * backend refuses — a band on a column that still carries its own rule — exist for a render. The
 * sheet stages the whole decision here and commits it through `applyExerciseAuthoring`, which is one
 * draft operation and one undo entry.
 *
 * ── The RULE and nothing else ───────────────────────────────────────────────────────────────
 * Ported from `move-with-me-frontend/.../plans/builder/model/progressionStaging.ts`, and trimmed on
 * 14 September 2026 to the seven actions a control on this sheet can actually dispatch. Four more
 * came across with the port and no surface ever reached them — a pin, a ladder percent, a base
 * value, the load's own unit — and an unreachable transition on a staged copy is worse than dead
 * code: it would mark the session dirty and then be dropped on the commit, which only writes rules
 * and bands. Mobile's `roleChange` and `setBandCell` never came at all, for the same reason stated
 * the other way round: mobile's editor is a full-screen surface that OWNS the exercise, while this
 * sheet stands beside the set table, which already authors both through their own operations and
 * their own undo entries.
 *
 * `restoreAuthoring` went with them. „Anuluj" does not restore the staged copy — it unmounts the
 * session, and the copy dies with it.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  axisForField,
  bandedGateOf,
  carriesBand,
  clearBand,
  clearColumnPins,
  columnStepParams,
  pinnedColumns,
  deltaRefusal,
  deltaUnitOptions,
  fromResultRefusal,
  measurementValueKey,
  ruleFor,
  ruleOnColumnPatch,
  writeBand,
  type ExerciseDraft,
  type ProgressionDeltaUnit,
  type ProgressionRule,
  type ProgressionRules,
  type SetDraft,
} from '~/entities/plan-draft';
import type { MeasurementFieldKey } from '~/shared/measurement';

/** Which family a column's rule reads as — the sheet's segmented condition. */
export type SentenceFamily = 'static' | 'fromResult';

/**
 * The slice of an exercise this sheet can change — and therefore exactly what dirtiness is measured
 * over. Nothing else on the exercise (its name, its slot token, its window, its coaching note, its
 * pins, the unit its load is priced in) is reachable from this surface, so nothing else is compared:
 * a sheet that watched the whole node would open dirty the moment a rename landed in the set table.
 */
interface StagedAuthoring {
  readonly sets: readonly SetDraft[];
  readonly progressionRules: ProgressionRules;
}

export type ProgressionStagingAction =
  /** Flip one column between „każdy cykl" and „gdy klient dowiezie" — the sheet's segmented condition. */
  | { readonly type: 'setColumnFamily'; readonly field: MeasurementFieldKey; readonly family: SentenceFamily }
  /** Take the rule off one column: the plan repeats cycle 1 there, which is a configuration, not a gap. */
  | { readonly type: 'clearColumnRule'; readonly field: MeasurementFieldKey }
  /**
   * „Brak progresji" — the rule comes off AND every per-cycle pin on this column goes with it, so the
   * column reads its base value flat in every cycle.
   *
   * It is a different act from `clearColumnRule` („Manualna"), and the difference is the whole reason
   * both exist: „Manualna" says "no rule, I will set the cycles myself", so the numbers the trainer
   * hand-set are exactly what must survive it. „Brak progresji" says "reset this column", and a reset
   * that left a column of pins standing would be a reset of nothing the trainer can see.
   */
  | { readonly type: 'clearColumnProgression'; readonly field: MeasurementFieldKey }
  /** The stepper and the chips — the size of the jump, in the column's own unit. */
  | { readonly type: 'setDelta'; readonly field: MeasurementFieldKey; readonly delta: number }
  /**
   * How the jump is MEASURED on one column: a flat number of the column's own units, or a percentage
   * that compounds. Only the load may take the second reading, and the refusal lives HERE rather
   * than only on the control, so no entry point can write the shape.
   *
   * It carries the STEP, which is what makes it one act rather than two: the trainer's step in the
   * unit they are flipping back to is the sheet's to remember, and two dispatches would let the
   * second land while the first was still in flight, leaving an `Absolute` rule holding a
   * percent-shaped number.
   */
  | {
      readonly type: 'setDeltaUnit';
      readonly field: MeasurementFieldKey;
      readonly unit: ProgressionDeltaUnit;
      readonly delta?: number;
    }
  /**
   * Bind the jump to a RANGE in another column, writing both ends across every banded row.
   *
   * `overwriteFloor` says the pair was TYPED as a pair. Without it the floor is only a seed for rows
   * carrying nothing, which is right when the gate merely MOVES and wrong when a trainer typed
   * "8–12" over rows already reading 10 and got "10–12" back.
   */
  | {
      readonly type: 'writeBand';
      readonly steppedField: MeasurementFieldKey;
      readonly gatedField: MeasurementFieldKey;
      readonly floor: number | null;
      readonly ceiling: number | null;
      readonly overwriteFloor?: boolean;
    }
  /**
   * Drop the CEILINGS and leave the rule's banded column standing — what an ERASED range field means.
   *
   * Deliberately NOT the same action as `unbindColumn`, and collapsing the two is the defect it
   * prevents: a trainer clearing the field is saying "no bar on these rows", while the switch beside
   * it says something else ("this rule does not wait for a column at all"). Letting an erased field
   * flip the switch would move a control the trainer never touched.
   */
  | { readonly type: 'clearBand' }
  /**
   * Turn the band OFF: the rule stops naming a column AND the ceilings go, in one act. The ceilings
   * come off through the WRITER, which orphans them the moment the band stops naming their column.
   */
  | { readonly type: 'unbindColumn'; readonly field: MeasurementFieldKey }
;

/**
 * Put a rule on one column — the WRITER's job, not this reducer's. `ruleOnColumnPatch` refuses a
 * column the movement does not measure or that another rule gates on, and drops the ceilings a
 * departing gate orphans; this only applies what it decided.
 */
function withColumnRule(
  state: ExerciseDraft,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): ExerciseDraft {
  const { patch } = ruleOnColumnPatch(state, field, rule);
  return patch.progressionRules === undefined ? state : { ...state, ...patch };
}

/**
 * The rule one column reads as under the other family, carrying the trainer's own step across. Null
 * only where `fromResultRefusal` names the one shape the wire has no room for.
 *
 * IT PICKS NO COLUMN. A rule entering the execution-gated family bands NOTHING: it advances when the
 * client delivers the whole set, which is the default reading (R3) and the only one true of every
 * movement. Choosing a column here is what silently takes reps away from a weight+reps exercise and
 * then refuses to give it back; the switch in the sheet is where a band is chosen.
 *
 * A rule ALREADY in this family keeps whatever it bands: the segmented control fires on every press,
 * including one re-picking the reading already in force, and nulling there would delete the
 * trainer's range for a tap that changed nothing.
 *
 * IT CARRIES THE STORED DOCUMENT. This is the one transition that rebuilds a rule from scratch
 * rather than spreading the current one, so dropping `wireDocument` here deletes a property a newer
 * backend put on a rule type this build knows — silently, on the next whole-tree save, for a trainer
 * who only pressed the segment their rule was already on. `carriedFields` is what decides whether the
 * document still applies: it is guarded on the TYPE, so a flip that genuinely changes the family
 * drops it, and a press that changes nothing keeps it.
 */
function ruleInFamily(
  state: ExerciseDraft,
  field: MeasurementFieldKey,
  family: SentenceFamily,
): ProgressionRule | null {
  const current = ruleFor(state.progressionRules, field);
  const currentUnit = current?.deltaUnit ?? 'Absolute';
  // THE UNIT IS NORMALISED, THE NUMBER IS NOT. `rulesFromWire` reads the document's `deltaUnit` on
  // every column with no field check, so a legacy plan can arrive with `Percent` on repetitions — a
  // shape the backend refuses outright. Carrying it through the flip would preserve that 400
  // forever, and this is the one act that touches every column's rule.
  const deltaUnit = deltaUnitOptions(field).includes(currentUnit) ? currentUnit : 'Absolute';
  const delta = current?.delta ?? columnStepParams(field, deltaUnit)?.initial ?? null;
  const wireDocument = current?.wireDocument;
  // R4 AGAIN, AND IT IS THE FAMILY THAT TRIPS IT, not only the stepper. „Statyczna" on a column with
  // no rule SEEDS that column's opening step, and on this sheet it could not do otherwise: a flat
  // static rule carries no progression by `carriesProgression`, so it draws no sentence and reads
  // exactly like „Bez reguły". Writing one here would answer the press with a control that does not
  // appear and no reason anywhere. Refused instead, the way the execution-gated family already is,
  // so the menu entry states why.
  if (family === 'static') {
    return deltaRefusal(state, field, null) === null
      ? { wireDocument, type: 'Static', delta, deltaUnit }
      : null;
  }
  if (fromResultRefusal(state, field) !== null) return null;
  const banded = current !== null && current.type === 'PreviousExecution' ? current.banded : null;
  // A from-the-result rule always states a step, because the wire's document does. Zero is the
  // honest reading of "advance by nothing" for a rule that has not been given one yet.
  return { wireDocument, type: 'PreviousExecution', banded, delta: delta ?? 0, deltaUnit };
}

export function stagedReducer(
  state: ExerciseDraft,
  action: ProgressionStagingAction,
): ExerciseDraft {
  switch (action.type) {
    case 'setColumnFamily': {
      // Both refusals the WRITER states — the column another rule gates on, and the second
      // execution-gated rule an exercise has no room for — are re-asked here rather than only on the
      // control that offers them, so no entry point can write the shape.
      const rule = ruleInFamily(state, action.field, action.family);
      return rule === null ? state : withColumnRule(state, action.field, rule);
    }

    case 'clearColumnRule':
      return withColumnRule(state, action.field, null);

    case 'clearColumnProgression': {
      const ruleless = withColumnRule(state, action.field, null);
      const axis = axisForField(action.field, ruleless.prescriptionMode);
      // A column with no axis holds no pins to take off — the rule removal above is the whole act.
      if (axis === null) return ruleless;
      return {
        ...ruleless,
        progressionOverrides: clearColumnPins(
          ruleless.progressionOverrides,
          ruleless.sets,
          axis,
        ),
      };
    }

    case 'setDelta': {
      const current = ruleFor(state.progressionRules, action.field);
      if (current === null) return state;
      // The REFUSAL lives here as well as on the stepper, for the reason the percent unit's does: an
      // exercise pricing its rows above the client's whole max may not also ask that price to climb
      // (R4), and a shape the backend throws on must be unreachable from every entry point rather
      // than merely un-offered by one control.
      if (deltaRefusal(state, action.field, current) !== null) return state;
      return withColumnRule(state, action.field, { ...current, delta: action.delta });
    }

    case 'setDeltaUnit': {
      const current = ruleFor(state.progressionRules, action.field);
      if (current === null) return state;
      // The REFUSAL lives here, not on the control that offers the choice: a percent delta on any
      // column but the load is a document the backend throws on.
      if (!deltaUnitOptions(action.field).includes(action.unit)) return state;
      // CHANGING THE UNIT NEVER INVENTS A STEP. A flat `Static` rule is the shape every saved
      // exercise comes back in, and seeding the new unit's opening step here turns a tap on the
      // segment into a real jump the trainer never wrote: tapping „%" to read its note would author
      // +5 %, and tapping back +2,5 kg, so an exercise with no progression starts climbing.
      //
      // A STEP OF ZERO IS NOT A STEP EITHER. An execution-gated rule spells "advance by nothing" as
      // zero because its wire delta is not nullable, so counting one as stepped lets the same defect
      // back in through a narrower door.
      const isStepped = current.delta !== null && current.delta !== 0;
      const delta = isStepped
        ? (action.delta ?? columnStepParams(action.field, action.unit)?.initial ?? null)
        : current.delta;
      const rule: ProgressionRule =
        current.type === 'PreviousExecution'
          ? { ...current, delta: delta ?? 0, deltaUnit: action.unit }
          : { ...current, delta, deltaUnit: action.unit };
      return withColumnRule(state, action.field, rule);
    }

    case 'writeBand': {
      const current = ruleFor(state.progressionRules, action.steppedField);
      if (current === null || current.type !== 'PreviousExecution') return state;
      // The gate moves WITH the band, in one act: the ceilings are numbers in the gated column's
      // unit, so a gate written apart from the rows it measures is a range nobody can read.
      const regated = withColumnRule(state, action.steppedField, {
        ...current,
        banded: action.gatedField,
      });
      return {
        ...regated,
        sets: writeBand(
          regated.sets,
          action.gatedField,
          action.floor,
          action.ceiling,
          action.overwriteFloor ?? false,
        ),
      };
    }

    case 'clearBand':
      return { ...state, sets: clearBand(state.sets) };

    case 'unbindColumn': {
      const current = ruleFor(state.progressionRules, action.field);
      if (current === null || current.type !== 'PreviousExecution' || current.banded === null) {
        return state;
      }
      // The ceilings come off through the WRITER, not here: losing the band is exactly the case it
      // already reads as orphaning them, and saying it twice would be two places to get it wrong.
      return withColumnRule(state, action.field, { ...current, banded: null });
    }

    default: {
      // THE COMPILE-TIME BRACE. A bare `default: return state` makes a new action shipped without
      // its case a silent no-op — a control that fires, dispatches and changes nothing, with only a
      // test between it and the trainer. `action` narrows to `never` here only while every member of
      // the union is handled above.
      const unhandled: never = action;
      void unhandled;
      return state;
    }
  }
}

/**
 * The band the trainer TYPED, carried from the range field to the commit. Null while they have not
 * touched it — a band merely LOOKED AT must not flatten a wave of 10 / 8 / 6.
 */
export interface StagedBand {
  /** The column the client climbs — the one the ceilings are written on. */
  readonly gatedField: MeasurementFieldKey;
  readonly floor: number | null;
  readonly ceiling: number | null;
  /** True only where the pair was typed as a pair. See `ExerciseAuthoring.band`. */
  readonly overwriteFloor: boolean;
}

/** One column's share of what „Zastosuj do draftu" commits — the shape `applyExerciseAuthoring` takes. */
export interface StagedCommit {
  readonly field: MeasurementFieldKey;
  readonly rule: ProgressionRule | null;
  readonly band: StagedBand | null;
  /** „Brak progresji" chose this column: its pins travel with the rule removal, in the same act. */
  readonly clearPins: boolean;
}

/**
 * Whether one column carries a per-cycle pin — the difference between „Manualna" and „Brak progresji"
 * on a column that has no rule either way.
 *
 * Both readings are "no rule". What tells them apart is whether the trainer has hand-set any cycle:
 * a column with pins is being driven by hand („Manualna"), and a column with none is simply flat
 * („Brak progresji", the mock's „płaskie"). Deriving it means the menu's selected entry is a reading
 * of the document rather than a memory of which entry was last pressed, so it survives an undo, a
 * reopen and a commit.
 */
export function columnCarriesPins(
  exercise: Pick<ExerciseDraft, 'progressionOverrides' | 'sets' | 'prescriptionMode'>,
  field: MeasurementFieldKey,
): boolean {
  const axis = axisForField(field, exercise.prescriptionMode);
  if (axis === null) return false;
  return pinnedColumns(exercise.progressionOverrides, exercise.sets).includes(axis.kind);
}

/**
 * Which measurement COLUMNS an exercise carries pins on.
 *
 * `pinnedColumns` answers in AXES, and the %1RM axis names no measurement field — it is the load
 * column priced the other way. So the one member that is not already a field is mapped back to the
 * column it is a reading of, which is what a caller asking "which columns did the trainer hand-set"
 * means by the question.
 */
function availablePinnedFields(
  exercise: Pick<ExerciseDraft, 'progressionOverrides' | 'sets'>,
): MeasurementFieldKey[] {
  return pinnedColumns(exercise.progressionOverrides, exercise.sets).map((kind) =>
    kind === 'percent' ? 'weight' : kind,
  );
}

/** Two rules, compared by what they SAY rather than by the object they are. */
function sameRule(before: ProgressionRule | null, after: ProgressionRule | null): boolean {
  if (before === null || after === null) return before === after;
  if (before.type !== after.type) return false;
  const beforeBanded = before.type === 'PreviousExecution' ? before.banded : null;
  const afterBanded = after.type === 'PreviousExecution' ? after.banded : null;
  return (
    beforeBanded === afterBanded &&
    before.delta === after.delta &&
    before.deltaUnit === after.deltaUnit
  );
}

/**
 * EVERY column this session changed, and the one column the band goes with — what „Zastosuj do
 * draftu" commits.
 *
 * ── Why it is a list ────────────────────────────────────────────────────────────────────────
 * The chips at the head of the sheet let a trainer author the load, then look at repetitions and
 * take its rule off, then press „Zastosuj". Committing only the column on screen dropped the other
 * edit under an announcement saying the rule had been applied, and the sheet closed on the same
 * press, so nothing on screen disagreed. The list is derived from the SNAPSHOT rather than from a
 * record of which chips were pressed, because a column edited back to where it started is not a
 * change and must not cost an entry on the wire.
 *
 * ── Why the band travels with a RULE and not with the session ────────────────────────────────
 * A ceiling names no column of its own: the `banded` field of some rule names it. So the band goes
 * to the one committed column whose rule bands `band.gatedField`, and to no other. A ceiling typed
 * while the load was on screen and then carried onto a rule that bands nothing wrote „climb to 12"
 * with nothing left that reads a 12. Where no committed rule bands that column, the band travels
 * NOWHERE and no ceiling is written.
 */
type CommitSide = Pick<
  ExerciseDraft,
  'progressionRules' | 'progressionOverrides' | 'sets' | 'prescriptionMode'
>;

export function stagedCommits(
  snapshot: CommitSide,
  staged: CommitSide,
  band: StagedBand | null,
): readonly StagedCommit[] {
  /* A column whose PINS the session dropped and whose rule never moved — „Brak progresji" on a column
   * that already had none — is a real edit with nothing in either rule map to find it by, so the
   * candidate list is the rules of both sides PLUS every column that was pinned when the sheet
   * opened. Without the third source that act committed nothing and the sheet closed saying it had. */
  const pinnedAtOpen = (Object.keys(snapshot.progressionRules) as MeasurementFieldKey[]).concat(
    availablePinnedFields(snapshot),
  );
  const fields = [
    ...new Set([
      ...pinnedAtOpen,
      ...(Object.keys(staged.progressionRules) as MeasurementFieldKey[]),
    ]),
  ];

  const host =
    band === null
      ? null
      : (fields.find((field) => {
          const rule = ruleFor(staged.progressionRules, field);
          return (
            rule !== null && rule.type === 'PreviousExecution' && rule.banded === band.gatedField
          );
        }) ?? null);

  /* A column „Brak progresji" reset: pinned when the sheet opened, unpinned in the staged copy. Read
   * as a DIFFERENCE rather than as a flag the reducer sets, for the same reason `changed` is: a
   * column cleared and then pinned back within one session has not been reset, and a record of which
   * entries were pressed would say it had. */
  const depinned = (field: MeasurementFieldKey) =>
    columnCarriesPins(snapshot, field) && !columnCarriesPins(staged, field);

  const changed = fields.filter(
    (field) =>
      depinned(field) ||
      !sameRule(ruleFor(snapshot.progressionRules, field), ruleFor(staged.progressionRules, field)),
  );

  const commits = changed.map<StagedCommit>((field) => ({
    field,
    rule: ruleFor(staged.progressionRules, field),
    clearPins: depinned(field),
    band: field === host ? band : null,
  }));

  // A ceiling retyped over a rule that did not otherwise move still has to reach the rows.
  if (host !== null && !changed.includes(host)) {
    commits.push({
      field: host,
      rule: ruleFor(staged.progressionRules, host),
      clearPins: false,
      band,
    });
  }
  return commits;
}

/**
 * The exercise the sheet's GRID is about: what the plan HOLDS, under the rule the sheet is AUTHORING.
 *
 * Two answers have to be on screen at once, and neither of them is a whole exercise. The staged copy
 * carries the rule and its band, and nothing else on it is live — it was seeded when the sheet opened
 * and the draft has moved under it since, because a set's role and every pin are written straight
 * through (see this module's header). The stored exercise carries those, and the rule the trainer is
 * in the middle of replacing.
 *
 * So the grid reads the stored node with two things laid over it: the staged RULE MAP, and the band's
 * numbers on the rows that may carry one. A row whose role changed under the open sheet keeps the new
 * role and loses a ceiling it can no longer hold, which is what `writeBand` would have done to it.
 *
 * Until 15 September 2026 the grid simply drew the stored exercise and a note said so. The owner's
 * call withdrew that: the mock's matrix follows the rule as it is written, and „+10 kg" that changes
 * no number until the sheet is closed is a preview that previews nothing.
 */
export function stagedProjection(stored: ExerciseDraft, staged: ExerciseDraft): ExerciseDraft {
  const gatedField = bandedGateOf(staged.progressionRules);
  const floorKey = gatedField === null ? null : measurementValueKey(gatedField);
  const authoredByLocalId = new Map(staged.sets.map((set) => [set.localId, set]));

  const sets = stored.sets.map((set) => {
    const authored = authoredByLocalId.get(set.localId);
    if (authored === undefined) return set;
    const banded: SetDraft = {
      ...set,
      prescribedCeiling: carriesBand(set) ? authored.prescribedCeiling : null,
    };
    return floorKey === null || !carriesBand(set)
      ? banded
      : { ...banded, [floorKey]: authored[floorKey] };
  });

  return { ...stored, progressionRules: staged.progressionRules, sets };
}

/** The slice the sheet can move — read off the staged copy, never assembled a second way. */
function stagedAuthoring(staged: ExerciseDraft): StagedAuthoring {
  return { sets: staged.sets, progressionRules: staged.progressionRules };
}

/**
 * Whether the staged copy says anything the snapshot did not — what „Zastosuj" is enabled by, and
 * what a dismissed sheet has to ask about before throwing the copy away.
 *
 * It compares the AUTHORED SLICE rather than the node, so a rename that landed in the set table
 * while the sheet was open does not read as an edit the sheet made.
 *
 * The rules are compared by what they SAY — `sameRule`, the same reading „Zastosuj" commits by — and
 * not by serialising the map. A rule also carries the document it arrived as, which is held for the
 * re-emit and is no part of what the trainer authored: a structural comparison reads a rebuilt rule
 * holding the same carried document as a change, and one that dropped it as none, which is both
 * halves of the question backwards.
 */
export function stagingIsDirty(snapshot: ExerciseDraft, staged: ExerciseDraft): boolean {
  const before = stagedAuthoring(snapshot);
  const after = stagedAuthoring(staged);
  const fields = new Set([
    ...(Object.keys(before.progressionRules) as MeasurementFieldKey[]),
    ...(Object.keys(after.progressionRules) as MeasurementFieldKey[]),
  ]);
  const rulesMoved = [...fields].some(
    (field) =>
      !sameRule(ruleFor(before.progressionRules, field), ruleFor(after.progressionRules, field)),
  );
  return (
    rulesMoved ||
    JSON.stringify(before.sets.map(bandedShape)) !== JSON.stringify(after.sets.map(bandedShape))
  );
}

/**
 * The part of a row this sheet can change — its type, its prescriptions and its ceiling.
 *
 * A row's whole shape would make the comparison depend on `localId`, which is minted per session and
 * never equal across a re-seed, so every sheet would open dirty.
 */
function bandedShape(set: SetDraft): unknown {
  return [
    set.setType,
    set.prescribedWeight,
    set.prescribedReps,
    set.prescribedDurationSeconds,
    set.prescribedDistanceMeters,
    set.prescribedRounds,
    set.prescribedRpe,
    set.prescribedRir,
    set.prescribedPercent,
    carriesBand(set) ? set.prescribedCeiling : null,
  ];
}
